import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Skeleton from '@mui/material/Skeleton';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useAppSelector, useAppDispatch } from 'store';
import { setCurrentTopic, setError, setLoading, setPopularDocuments, setPopularLoading, setTopicDetail } from 'store/topicDetailSlice';
import { fetchExploreTopic, fetchPopularDocuments } from 'services/confluenceService';
import TopicDetailSidebar from 'shared-compoents/topic-detail-sidebar';
import {
  PageWrapper,
  Breadcrumbs,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  TopicHeader,
  TopicIcon,
  TopicTitle,
  TopicDescription,
  SearchWrapper,
  SearchIcon,
  SearchInput,
  ContentLayout,
  MainContent,
  Sidebar,
  DocCount,
  DocumentCard,
  DocIconWrapper,
  DocContent,
  DocTitle,
  DocMeta,
  DocDate,
  SidebarCard,
  AboutTopicImage,
  AboutTopicTitle,
  AboutTopicDescription,
  AboutTopicStats,
  AboutTopicStatsIcon,
  AboutTopicStatsCount,
  AboutTopicStatsLabel,
} from './styles';

function TopicDetail() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.userData.access_token);
  const topic = useAppSelector((state) =>
    topicId ? state.topicDetail.topics[topicId] : null
  );
  const loading = useAppSelector((state) => state.topicDetail.loading);
  const error = useAppSelector((state) => state.topicDetail.error);
  const popularDocuments = useAppSelector((state) => state.topicDetail.popularDocuments);
  const popularLoading = useAppSelector((state) => state.topicDetail.popularLoading);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDocuments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const documents = topic?.documents ?? [];
    if (!query) return documents;
    return documents.filter((doc) =>
      doc.title.toLowerCase().includes(query) ||
      doc.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  }, [searchQuery, topic]);

  useEffect(() => {
    if (!topicId || !accessToken) return;

    dispatch(setCurrentTopic(topicId));
    dispatch(setLoading(true));
    fetchExploreTopic(topicId)
      .then((data) => dispatch(setTopicDetail(data)))
      .catch((requestError) => {
        dispatch(setError(requestError instanceof Error ? requestError.message : 'Failed to load topic.'));
      });

    dispatch(setPopularLoading(true));
    fetchPopularDocuments()
      .then((data) => dispatch(setPopularDocuments(data.documents)))
      .catch(() => dispatch(setPopularLoading(false)));
  }, [topicId, accessToken, dispatch]);

  if (!topic) {
    if (error && !loading) {
      return (
        <PageWrapper>
          <p>{error}</p>
        </PageWrapper>
      );
    }
    return (
      <PageWrapper>
        <Breadcrumbs>
          <BreadcrumbLink to="/explore-topics">Knowledge Hub</BreadcrumbLink>
          <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
          <BreadcrumbLink to="/explore-topics">Explore Topics</BreadcrumbLink>
        </Breadcrumbs>

        <ContentLayout>
          <MainContent>
            <TopicHeader>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="text" width={260} height={36} />
            </TopicHeader>
            <Skeleton variant="text" width="70%" height={22} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" width="100%" height={44} sx={{ mb: 3, maxWidth: 700 }} />
            <Skeleton variant="text" width={220} height={20} sx={{ mb: 2 }} />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" width="100%" height={72} sx={{ mb: 1.5 }} />
            ))}
          </MainContent>

          <Sidebar>
            <Skeleton variant="rounded" width="100%" height={170} />
            <Skeleton variant="rounded" width="100%" height={280} />
          </Sidebar>
        </ContentLayout>
      </PageWrapper>
    );
  }

  const renderTopicIcon = () => {
    if (topic.id === 'automation') {
      return <SettingsOutlinedIcon sx={{ fontSize: 32 }} />;
    }
    return topic.icon ? <img src={topic.icon} alt="" /> : null;
  };

  return (
    <PageWrapper>
      <Breadcrumbs>
        <BreadcrumbLink to="/explore-topics">Knowledge Hub</BreadcrumbLink>
        <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
        <BreadcrumbLink to="/explore-topics">Explore Topics</BreadcrumbLink>
        <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
        <BreadcrumbCurrent>{topic.title}</BreadcrumbCurrent>
      </Breadcrumbs>

      <ContentLayout>
        <MainContent>
          <TopicHeader>
            <TopicIcon>{renderTopicIcon()}</TopicIcon>
            <TopicTitle>{topic.title}</TopicTitle>
          </TopicHeader>
          <TopicDescription>{topic.description}</TopicDescription>

          <SearchWrapper>
            <SearchIcon src="/assets/search-grey.svg" alt="" />
            <SearchInput
              type="text"
              placeholder={`Search in ${topic.title}...`}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </SearchWrapper>

          <DocCount>
            {searchQuery.trim()
              ? `Showing ${filteredDocuments.length} of ${topic.documents.length} documents`
              : `Showing 1–${topic.documents.length} of ${topic.totalDocs} documents`}
          </DocCount>

          {filteredDocuments.map((doc) => (
            <DocumentCard
              key={doc.id}
              onClick={() => navigate(`/documentation/${doc.id}?from=${topic.id}`)}
            >
              <DocIconWrapper>
                <img src="/assets/documentation-icon.svg" alt="" width={24} height={24} />
              </DocIconWrapper>
              <DocContent>
                <DocTitle>{doc.title}</DocTitle>
                {/* <DocDescription>{doc.description}</DocDescription>
                <DocTags>
                  {doc.tags.map((tag) => (
                    <DocTag key={tag}>{tag}</DocTag>
                  ))}
                </DocTags> */}
              </DocContent>
              <DocMeta>
                <DocDate>Updated {doc.updatedAt}</DocDate>
              </DocMeta>
            </DocumentCard>
          ))}

          {searchQuery.trim() && filteredDocuments.length === 0 && (
            <DocCount>No documents match &quot;{searchQuery.trim()}&quot;.</DocCount>
          )}
        </MainContent>

        <Sidebar>
          <SidebarCard>
            <AboutTopicImage src="/assets/knowledge-hub.svg" alt="" />
            <AboutTopicTitle>About this topic</AboutTopicTitle>
            <AboutTopicDescription>{topic.description}</AboutTopicDescription>
            <AboutTopicStats>
              <AboutTopicStatsIcon>
                <img src="/assets/documentation-icon.svg" alt="" width={16} height={16} />
              </AboutTopicStatsIcon>
              <AboutTopicStatsCount>{topic.totalDocs}</AboutTopicStatsCount>
              <AboutTopicStatsLabel>Documents</AboutTopicStatsLabel>
            </AboutTopicStats>
          </SidebarCard>

          <TopicDetailSidebar
            popularDocuments={popularDocuments}
            loading={popularLoading}
            onSelectDocument={(pageId) => navigate(`/documentation/${pageId}?from=${topic.id}`)}
          />
        </Sidebar>
      </ContentLayout>
    </PageWrapper>
  );
}

export default TopicDetail;
